import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Tab from '@atlaskit/tabs/tab';
import TabList from '@atlaskit/tabs/tab-list';
import TabPanel from '@atlaskit/tabs/tab-panel';
import Tabs from '@atlaskit/tabs/tabs';
import { token } from '@atlaskit/tokens';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';

import { LoadingSkeleton } from './LoadingSkeleton';
import messages from './messages';
import { SEARCH_WORK_TABS, type SearchWorkEntityType } from './types';

const TAB_MESSAGE_KEY: Record<SearchWorkEntityType, keyof typeof messages> = {
	all: 'searchWorkAllTabLabel',
	project: 'searchWorkProjectsTabLabel',
	epic: 'searchWorkJiraTabLabel',
	goal: 'searchWorkGoalsTabLabel',
};

const styles = cssMap({
	body: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	searchContainer: {
		display: 'flex',
		height: '32px',
	},
	resultsViewport: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		minHeight: '340px',
	},
});

const NOOP_SEARCH = (): void => {};

export type SearchWorkPageLoadingProps = {
	fullScreen?: boolean;
	chatRefreshHeader?: React.ReactNode;
};

/** Loading state for the lazy Search Work page. */
export const SearchWorkPageLoading = ({
	fullScreen = false,
	chatRefreshHeader,
}: SearchWorkPageLoadingProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack testId="ai-mate-search-work-view-loading" alignBlock="start" grow="fill">
			{!fullScreen && chatRefreshHeader}
			<Stack xcss={styles.body} space="space.200">
				<Box xcss={styles.searchContainer}>
					<SearchInput
						placeholderText={formatMessage(messages.searchWorkInputPlaceholder)}
						onSearch={NOOP_SEARCH}
					/>
				</Box>
				<Tabs id="search-work-loading-tabs" testId="ai-mate-search-work-tabs-loading">
					<TabList>
						{SEARCH_WORK_TABS.map((tab) => (
							<Tab key={tab} testId={`ai-mate-search-work-tab-${tab}-loading`}>
								{formatMessage(messages[TAB_MESSAGE_KEY[tab]])}
							</Tab>
						))}
					</TabList>
					{SEARCH_WORK_TABS.map((tab) => (
						<TabPanel key={tab}>
							<Box xcss={styles.resultsViewport} testId="search-work-results-viewport-loading">
								<LoadingSkeleton />
							</Box>
						</TabPanel>
					))}
				</Tabs>
			</Stack>
		</Stack>
	);
};
